import { createSelector } from '@reduxjs/toolkit';

import { carrierDataSelectors } from 'wanderu-ducks-carriers';
import { createAppSelector } from 'wanderu-ducks-utils';

import SLICE_NAMESPACE from '../types';
import {
    buildEndpointData,
    buildFakeItinerary,
    calculateItinerary,
    getNumberOfMidnightsPassed,
    getRoutedTripFees,
    getRoutedTripPricing,
    getSingleTripFees,
    getSingleTripPricing,
    isRoutedTrip,
    tripDuration,
} from './helpers';
import NAMESPACE from './types';

const EMPTY_CARRIER_DATA = Object.freeze({});
const ROUTED_TRIP_DELIMITER = '|';

function selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function retrieveTripData(data, tripId) {
    return data.trips[tripId];
}

function retrieveTripItineraryData(appState, tripId) {
    return selectData(appState).tripItineraries[tripId];
}

function retrieveTripInformationData(appState, tripId) {
    return selectData(appState).tripInformation[tripId];
}

function mapDuration(tripObject) {
    if (!tripObject) {
        return undefined;
    }

    const {
        arrive_datetime_utc: arriveDateTime,
        depart_datetime_utc: departDateTime,
    } = tripObject;

    return {
        duration: tripDuration(departDateTime * 1000, arriveDateTime * 1000),
    };
}

/**
 * Pure function: builds endpoint data for the given direction ('arrive' or 'depart').
 * If complete itinerary data is available, overlays the itinerary's timestamp
 * onto the endpoint to account for operator-updated times.
 *
 * For 'arrive', uses the last itinerary leg; for 'depart', uses the first.
 */
function mapEndpointData(tripLeg, tripItinerary, direction) {
    if (!tripLeg || tripLeg.notFound) {
        return undefined;
    }

    let endpoint = buildEndpointData(tripLeg, direction);

    if (tripItinerary) {
        const { itinerary, metadata } = tripItinerary;

        if (metadata && metadata.complete && itinerary && itinerary.length) {
            const leg = direction === 'arrive'
                ? itinerary[itinerary.length - 1]
                : itinerary[0];

            if (leg?.timestamp) {
                endpoint = {
                    ...endpoint,
                    localizedDateTime: leg.timestamp,
                };
            }
        }
    }

    return endpoint;
}

// Hand-rolled cache because getCarrierCodes is called inside an input selector
// (selectTripItinerary), where a nested memoized selector would complicate the
// dependency graph. WeakMap keyed by tripData reference — entries are GC'd when
// trip data is evicted from the store. Assumes tripData objects are immutable.
const carrierCodesCache = new WeakMap();

function getCarrierCodes(tripData) {
    if (!tripData || tripData.notFound) {
        return undefined;
    }

    if (carrierCodesCache.has(tripData)) {
        return carrierCodesCache.get(tripData);
    }

    let codes;
    if (isRoutedTrip(tripData)) {
        codes = [...tripData.triplegs
            .reduce((carrierCodes, leg) => {
                if (!leg.trip_id) {
                    return carrierCodes;
                }

                return carrierCodes.add(leg.carrier);
            }, new Set())];
    } else {
        codes = [tripData.carrier];
    }

    carrierCodesCache.set(tripData, codes);
    return codes;
}

/**
 * @param {Object} itineraryObject - raw trip itin info
 * @param {Object} tripData - raw trip data
 * @param {Object} carrierData - mapped carrier data keyed by carrier code, for the trip's carriers
 * @return {Object|Array<Object>|undefined} a single {itinerary, metadata} object for regular trips,
 *   or an array of such objects for routed trips. Returns undefined if tripData is missing.
 */
function mapItinerary(itineraryObject, tripData, carrierData) {
    if (!tripData || tripData.notFound) {
        return undefined;
    }

    if (isRoutedTrip(tripData)) {
        return tripData.triplegs
            .filter((leg) => !!leg.trip_id)
            .reduce((itins, leg) => [
                ...itins,
                ...mapItinerary(leg.itinerary_info, leg, carrierData),
            ], []);
    }

    /* we can't always prioritize dynamically-fetched itin info over what's
     * stored inside the psearch result.  some trips on some carriers will cause
     * an empty array to be returned by wapi's itineraryinfo.json endpoint (e.g.
     * the only itin info fetched from the carrier's api gets filtered out by
     * wapi for one reason or another before satisfying our request) and in that
     * case we need to fall back to the data in the psearch result... */
    let source = itineraryObject;

    if (!source || !source.itinerary || !source.itinerary.length) {
        source = tripData.itinerary_info
            || buildFakeItinerary(tripData, carrierData[tripData.carrier]);
    }

    if (!source) {
        return undefined;
    }

    /* ...but we DO need to prioritize the dynamic metadata over the psearch
     * metadata, to indicate that we've looked for more data but didn't really
     * get any */
    const metadata = itineraryObject
        ? itineraryObject.metadata
        : source.metadata;

    return {
        itinerary: calculateItinerary(source.itinerary, tripData),
        metadata: {
            ...metadata,
            tripId: tripData.trip_id,
        },
    };
}

const selectTripData = createAppSelector(
    [
        selectData,
        (_, tripId) => tripId,
    ],
    (data, tripId) => retrieveTripData(data, tripId),
);

const selectTripItinerary = createAppSelector(
    [
        selectTripData,
        (state, tripId) => retrieveTripItineraryData(state, tripId),
        (state, tripId) => {
            const tripData = selectTripData(state, tripId);
            const carrierCodes = getCarrierCodes(tripData);

            return carrierDataSelectors.selectCarrierData(state, carrierCodes);
        },
    ],
    (tripData, itinData, carrierData) => mapItinerary(itinData, tripData, carrierData),
);

function selectTripInformation(appState, tripId) {
    return retrieveTripInformationData(appState, tripId);
}

const selectLegIds = createAppSelector(
    [
        selectTripData,
        (_, tripId) => tripId,
    ],
    (trip, tripId) => {
        if (!tripId) {
            return undefined;
        }
        if (!trip) {
            return tripId.split(ROUTED_TRIP_DELIMITER);
        }

        return trip.triplegs
            ? trip.triplegs.map((leg) => leg.trip_id).filter(Boolean)
            : [tripId];
    },
);

function selectLegByDirection(state, tripId, direction) {
    const legIds = selectLegIds(state, tripId);

    if (!legIds?.length) return undefined;

    return direction === 'arrive'
        ? legIds[legIds.length - 1]
        : legIds[0];
}

function selectForLeg(selector, direction) {
    return (state, tripId) => {
        const legId = selectLegByDirection(state, tripId, direction);

        return legId
            ? selector(state, legId)
            : undefined;
    };
}

const selectTripArriveData = createAppSelector(
    [
        selectForLeg(selectTripData, 'arrive'),
        selectForLeg(selectTripItinerary, 'arrive'),
    ],
    (tripLeg, tripItinerary) => mapEndpointData(tripLeg, tripItinerary, 'arrive'),
);

const selectTripCarrierData = createAppSelector(
    [
        (state, tripId) => {
            const tripData = selectTripData(state, tripId);

            return tripData
                ? carrierDataSelectors.selectCarrierData(state, tripData.carrier)
                : undefined;
        },
    ],
    (carrierData) => carrierData || EMPTY_CARRIER_DATA,
);

const selectTripDepartData = createAppSelector(
    [
        selectForLeg(selectTripData, 'depart'),
        selectForLeg(selectTripItinerary, 'depart'),
    ],
    (tripLeg, tripItinerary) => mapEndpointData(tripLeg, tripItinerary, 'depart'),
);

const selectTripDuration = createAppSelector(
    [selectTripData],
    (tripData) => mapDuration(tripData),
);

const selectAllTripIds = createSelector(
    [selectData],
    (data) => (data
        ? Object.keys(data.trips)
        : []),
);

const selectTripNumberOfMidnightsPassed = createAppSelector(
    [
        selectTripArriveData,
        selectTripDepartData,
    ],
    (arrive, depart) => {
        const { localizedDateTime: arriveDateTime } = arrive || {};
        const { localizedDateTime: departDateTime } = depart || {};

        return getNumberOfMidnightsPassed(departDateTime, arriveDateTime);
    },
);

const selectTripDataPricing = createAppSelector(
    [selectTripData],
    (tripData) => {
        if (!tripData) {
            return undefined;
        }

        const isRouted = isRoutedTrip(tripData);
        const pricingData = isRouted
            ? tripData.triplegs.map((leg) => leg.pricing)
            : tripData.pricing;

        const pricing = isRouted
            ? getRoutedTripPricing(pricingData)
            : getSingleTripPricing(pricingData);

        const fees = isRouted
            ? getRoutedTripFees(pricingData)
            : getSingleTripFees(pricingData);

        return { fees, pricing };
    },
);

export {
    getCarrierCodes,
    selectAllTripIds,
    selectLegIds,
    selectTripArriveData,
    selectTripCarrierData,
    selectTripData,
    selectTripDataPricing,
    selectTripDepartData,
    selectTripDuration,
    selectTripInformation,
    selectTripItinerary,
    selectTripNumberOfMidnightsPassed,
};
